import { FormControl, Grid, TextField, Typography } from '@mui/material';
import React from 'react';
import { Controller, DeepPartial, useForm, UseFormReturn } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import {
  CreateTenantWorkOrderSubmissionRequest,
  TenantWorkOrderSubmission,
  UpdateTenantWorkOrderSubmissionRequest,
} from 'vendor-marketplace-shared-library';

import Button from '../../../../components/common/styled-mui-overrides/Button';
import theme from '../../../../utilities/theme';

type TenantUserDetailsFormProps = {
  onSubmit: (data: any) => void;
  tenantWorkOrderSubmission: TenantWorkOrderSubmission | null;
  loading: boolean;
};
const TenantUserDetailsForm = (props: TenantUserDetailsFormProps) => {
  const [_, setSearchParams] = useSearchParams();
  const form: UseFormReturn<
    CreateTenantWorkOrderSubmissionRequest | UpdateTenantWorkOrderSubmissionRequest
  > = useForm({
    defaultValues: {
      tenantFirstName: '',
      tenantLastName: '',
      tenantEmail: '',
    } as DeepPartial<any>,
  });

  return (
    <form onSubmit={form.handleSubmit(props.onSubmit)}>
      <Grid container sx={styles.mainContainer}>
        <Grid item>
          <Typography variant='h5' fontWeight='bold'>
            Your details
          </Typography>
        </Grid>

        {/*First Name Input Field*/}
        <Grid item sx={styles.fullWidth}>
          <Controller
            name='tenantFirstName'
            control={form.control}
            render={({ field }) => {
              return (
                <FormControl fullWidth>
                  <Typography variant='subtitle2'>First name</Typography>
                  <TextField
                    label=''
                    size='small'
                    inputProps={{ 'aria-label': 'Without label' }}
                    placeholder='Ex: John'
                    {...field}
                  />
                </FormControl>
              );
            }}
          />
        </Grid>

        {/*Last Name Input Field*/}
        <Grid item sx={styles.fullWidth}>
          <Grid container sx={styles.inputContainer}>
            <Grid item>
              <Typography variant='subtitle2'>Last name</Typography>
            </Grid>
            <Grid item>
              <Controller
                name='tenantLastName'
                control={form.control}
                render={({ field }) => {
                  return (
                    <FormControl fullWidth>
                      <TextField
                        label=''
                        size='small'
                        inputProps={{ 'aria-label': 'Without label' }}
                        placeholder='Ex: Doe'
                        {...field}
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/*Email Input Field*/}
        <Grid item sx={styles.fullWidth}>
          <Grid container sx={styles.inputContainer}>
            <Grid item>
              <Typography variant='subtitle2'>Email</Typography>
            </Grid>
            <Grid item>
              <Controller
                name='tenantEmail'
                control={form.control}
                render={({ field }) => {
                  return (
                    <FormControl fullWidth>
                      <TextField
                        label=''
                        size='small'
                        required
                        inputProps={{ 'aria-label': 'Without label' }}
                        placeholder='Ex: johndoe@provider.com'
                        {...field}
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item sx={styles.pageCounterContainer}>
          <Typography sx={styles.greyText} variant='caption'>
            3/3
          </Typography>
        </Grid>

        {/*Back and Next buttons*/}
        <Grid item sx={styles.fullWidth}>
          <Grid container sx={styles.buttonContainer}>
            <Grid item sx={styles.flexOne}>
              <Button
                sx={styles.backButton}
                onClick={() => {
                  setSearchParams({ step: '2' });
                }}
              >
                Back
              </Button>
            </Grid>
            <Grid item sx={styles.flexOne}>
              <Button type='submit'>Next</Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default TenantUserDetailsForm;

const styles = {
  mainContainer: {
    flexDirection: 'column',
    rowGap: 4,
    alignItems: 'center',
  },
  fullWidth: {
    width: '100%',
  },
  inputContainer: {
    flexDirection: 'column',
    rowGap: 1,
  },
  pageCounterContainer: {
    paddingTop: 4,
  },
  greyText: {
    color: theme.palette.secondary.main,
  },
  backButton: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.common.black,
    border: `1px ${theme.palette.common.black} solid`,
    '&:hover': {
      backgroundColor: theme.palette.secondary.light,
    },
  },
  flexOne: {
    flex: 1,
  },
  buttonContainer: {
    columnGap: 1,
  },
};
